import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Building2, Briefcase, Hammer, Lightbulb, User } from 'lucide-react';
import { api } from '@/lib/api';
import { qk } from '@/lib/queryClient';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { relativeTime } from '@/lib/format';
import type { ProjectDto, ProjectType, ProjectStatus } from '@cc/types';
import { cn } from '@/lib/cn';

const TYPE_ICONS: Record<ProjectType, typeof Building2> = {
  BUSINESS: Briefcase,
  PROPERTY: Building2,
  DECISION: Lightbulb,
  CREATIVE: Hammer,
  PERSONAL: User,
};

const STATUS_VARIANTS: Record<ProjectStatus, 'success' | 'warning' | 'muted'> = {
  ACTIVE: 'success',
  DORMANT: 'warning',
  COMPLETE: 'muted',
};

export function ProjectsPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: qk.projects,
    queryFn: () => api.listProjects(),
  });

  const projects = data?.projects ?? [];
  const grouped = groupByStatus(projects);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Projects</h1>
        <p className="text-sm text-muted-foreground">
          The 9 v1 projects. Edit details, aliases, and entities in Settings, Projects.
        </p>
      </div>

      {error && (
        <p className="text-sm text-destructive">Failed to load projects, please retry.</p>
      )}

      {isLoading ? (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
      ) : (
        <motion.div
          initial="hidden"
          animate="visible"
          variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.06 } } }}
          className="space-y-6"
        >
          {(['ACTIVE', 'DORMANT', 'COMPLETE'] as ProjectStatus[]).map((status) => {
            const list = grouped.get(status) ?? [];
            if (list.length === 0) return null;
            return (
              <section key={status} className="space-y-2">
                <div className="flex items-center gap-2">
                  <h2 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    {status.toLowerCase()}
                  </h2>
                  <span className="text-xs text-muted-foreground/70">({list.length})</span>
                </div>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  {list.map((p) => (
                    <ProjectCard key={p.id} project={p} />
                  ))}
                </div>
              </section>
            );
          })}
        </motion.div>
      )}
    </div>
  );
}

function ProjectCard({ project }: { project: ProjectDto }) {
  const Icon = TYPE_ICONS[project.type];
  const statusVariant = STATUS_VARIANTS[project.status];
  return (
    <motion.div
      variants={{
        hidden: { opacity: 0, y: 6 },
        visible: {
          opacity: 1,
          y: 0,
          transition: { type: 'spring', stiffness: 120, damping: 14 },
        },
      }}
      whileHover={{ y: -2 }}
    >
      <Link to={`/projects/${project.id}`} className="block">
        <Card className={cn('h-full transition-colors hover:border-foreground/20')}>
          <CardContent className="p-5">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 min-w-0">
                <div className="mt-0.5 flex h-8 w-8 flex-none items-center justify-center rounded-md bg-muted">
                  <Icon className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <p className="font-medium leading-tight">{project.name}</p>
                  <p className="mt-1 text-xs text-muted-foreground line-clamp-2">
                    {project.description || 'No description yet.'}
                  </p>
                </div>
              </div>
              <Badge variant={statusVariant}>{project.status.toLowerCase()}</Badge>
            </div>
            <div className="mt-4 flex items-center gap-4 text-[11px] text-muted-foreground">
              <span>{project.type.toLowerCase()}</span>
              <span>{project.activityCount} activities</span>
              <span>last: {relativeTime(project.lastActivityAt)}</span>
            </div>
          </CardContent>
        </Card>
      </Link>
    </motion.div>
  );
}

function groupByStatus(projects: ProjectDto[]): Map<ProjectStatus, ProjectDto[]> {
  const out = new Map<ProjectStatus, ProjectDto[]>();
  for (const p of projects) {
    const list = out.get(p.status) ?? [];
    list.push(p);
    out.set(p.status, list);
  }
  return out;
}
